import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../components/ui/collapsible";
import {
  Sparkles,
  Send,
  Trash2,
  Brain,
  Activity,
  ListTree,
  Plus,
  User2,
  Wrench,
  RefreshCw,
  Phone,
  Mail,
  MessagesSquare,
  Database,
  Cpu,
  Filter,
  Pause,
  Play,
  Copy,
  Ban,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useConferma } from "../components/ConfirmProvider";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'erano DUE copie locali).
import { formatDate, formatDateTime } from "../lib/format";

const CHANNEL_INSUREASSIST = "insureassist";

// =====================================================================
//  Stato — status dei servizi di Sara
// =====================================================================

function tonefor(ok) {
  if (ok === true) return "ok";
  if (ok === false) return "bad";
  return "dim";
}

function StatusDot({ tone, className = "" }) {
  const colors = {
    ok: "bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.18)]",
    bad: "bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)]",
    warn: "bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.18)]",
    dim: "bg-muted-foreground/50",
  };
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${colors[tone] || colors.dim} ${className}`}
    />
  );
}

function ServiceCard({ icon: Icon, name, tone, statusLabel, metrics = [], description, children }) {
  return (
    <Card className="transition-shadow hover:shadow-md" data-testid={`sara-service-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="p-4 sm:p-5 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-lg bg-muted flex items-center justify-center">
              {Icon ? <Icon className="h-4 w-4" /> : null}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{name}</div>
              {description && <div className="text-xs text-muted-foreground truncate">{description}</div>}
            </div>
          </div>
          <Badge variant="outline" className="gap-1.5 shrink-0">
            <StatusDot tone={tone} />
            {statusLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-5 pt-0 space-y-3">
        {metrics.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {metrics.map((m, i) => (
              <div key={i} className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">{m.label}</span>
                <span className="font-mono text-xs sm:text-sm break-all">{m.value || "—"}</span>
              </div>
            ))}
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function StatoTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyBriefing, setBusyBriefing] = useState(false);

  async function reload() {
    try {
      const { data } = await axios.get("/api/sara/status");
      setStatus(data);
    } catch (err) {
      setStatus({ ok: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    const t = setInterval(reload, 10_000);
    return () => clearInterval(t);
  }, []);

  async function handleBriefing() {
    setBusyBriefing(true);
    try {
      const { data } = await axios.post("/api/sara/actions/briefing");
      if (data?.skipped) {
        toast.info("Briefing gi\u00e0 inviato oggi (dedup attivo).");
      } else {
        toast.success("Briefing inviato su Telegram.");
      }
    } catch (err) {
      toast.error("Errore briefing: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusyBriefing(false);
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-32 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const llm = status?.llm || {};
  const tg = status?.telegram || {};
  const wa = status?.whatsapp || {};
  const em = status?.email || {};
  const queues = status?.queue || {};

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold font-heading">Servizi di Sara</h3>
          <p className="text-sm text-muted-foreground">
            Stato dei provider LLM, della memoria e dei canali (Telegram, WhatsApp, Email).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} data-testid="sara-refresh-status-button">
            <RefreshCw className="h-3.5 w-3.5 mr-2" />
            Aggiorna
          </Button>
          <Button size="sm" onClick={handleBriefing} disabled={busyBriefing} data-testid="sara-trigger-briefing-button">
            <Send className="h-3.5 w-3.5 mr-2" />
            {busyBriefing ? "Invio…" : "Invia briefing ora"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <ServiceCard
          icon={Cpu}
          name="Modello (Strata)"
          description={llm.target}
          tone={tonefor(llm.reachable)}
          statusLabel={llm.reachable ? "Connesso" : "Non raggiungibile"}
          metrics={[
            { label: "Modello", value: llm.model },
            { label: "Latenza health", value: status?.latency_ms ? `${status.latency_ms} ms` : "—" },
            { label: "Contesto", value: llm.max_context ? `${Math.round(llm.max_context / 1024)}K token` : "—" },
          ]}
        />
        <ServiceCard
          icon={Database}
          name="MongoDB"
          description="Memoria persistente di Sara"
          tone={tonefor(status?.mongo)}
          statusLabel={status?.mongo ? "Connesso" : "Down"}
          metrics={[
            { label: "DB", value: "insureassist" },
            { label: "Latenza", value: status?.latency_ms ? `${status.latency_ms} ms` : "—" },
          ]}
        />
        <ServiceCard
          icon={MessagesSquare}
          name="Telegram"
          description="Canale principale (vocali, comandi)"
          tone={tonefor(tg.enabled)}
          statusLabel={tg.enabled ? "Attivo" : "Non configurato"}
          metrics={[{ label: "Coda", value: String(queues.telegram ?? 0) }]}
        />
        <ServiceCard
          icon={Phone}
          name="WhatsApp"
          description="Baileys monitor"
          tone={tonefor(wa.enabled ? wa.status === "connected" : null)}
          statusLabel={wa.enabled ? wa.status || "unknown" : "Disabilitato"}
          metrics={[
            { label: "Status", value: wa.status || "—" },
            { label: "Coda", value: String(queues.whatsapp ?? 0) },
          ]}
        />
        <ServiceCard
          icon={Mail}
          name="Email IMAP"
          description="Inbox monitor"
          tone={tonefor(em.enabled)}
          statusLabel={em.enabled ? "Connesso" : "Disabilitato"}
        />
        <ServiceCard
          icon={Sparkles}
          name="Tool & Skill"
          description="Function calling nativo"
          tone={tonefor(true)}
          statusLabel={`${(status?.tools || []).length} tool`}
          metrics={[
            { label: "Skill installate", value: (status?.installed_skills || []).length || "0" },
            { label: "Provider", value: llm.provider || "—" },
          ]}
        />
      </div>
    </div>
  );
}

// =====================================================================
//  Chat
// =====================================================================

function ToolCallBlock({ tc }) {
  const fnName = tc.function?.name || tc.name || "tool";
  const argsRaw = tc.function?.arguments ?? tc.arguments ?? {};
  let argsPretty;
  try {
    argsPretty =
      typeof argsRaw === "string"
        ? JSON.stringify(JSON.parse(argsRaw), null, 2)
        : JSON.stringify(argsRaw, null, 2);
  } catch {
    argsPretty = String(argsRaw);
  }
  return (
    <Collapsible className="border border-border rounded-lg bg-muted/50 my-2">
      <CollapsibleTrigger className="w-full px-3 py-2 flex items-center justify-between gap-2 text-xs hover:bg-muted transition-colors rounded-lg">
        <span className="flex items-center gap-2">
          <Wrench className="h-3.5 w-3.5" />
          <span className="font-medium font-mono">{fnName}</span>
        </span>
        <span className="text-muted-foreground">vedi argomenti</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-words border-t border-border">{argsPretty}</pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

function MessageBubble({ msg }) {
  const role = msg.role;
  if (role === "tool") {
    return (
      <div className="flex justify-center">
        <div className="text-xs text-muted-foreground bg-muted/60 border border-border rounded-full px-3 py-1">
          Risultato: <span className="font-mono">{msg.name || "tool"}</span> → {String(msg.content || "").slice(0, 120)}
          {(msg.content || "").length > 120 ? "…" : ""}
        </div>
      </div>
    );
  }
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
          isUser ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
        }`}
      >
        {isUser ? <User2 className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </div>
      <div className={`max-w-[80%] ${isUser ? "items-end" : "items-start"} flex flex-col`}>
        <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
          <span>{isUser ? "Tu" : "Sara"}</span>
          {msg.created_at && (
            <span className="font-mono">
              {new Date(msg.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div
          className={
            "px-4 py-3 rounded-2xl text-sm leading-6 break-words " +
            (isUser ? "bg-primary text-primary-foreground" : "bg-card border border-border")
          }
        >
          {msg.content ? (
            isUser ? (
              <span className="whitespace-pre-wrap">{msg.content}</span>
            ) : (
              <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-2 prose-li:my-0">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              </div>
            )
          ) : (
            !isUser && <span className="text-muted-foreground">(risposta vuota)</span>
          )}
        </div>
        {Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0 && (
          <div className="w-full">
            {msg.tool_calls.map((tc, i) => (
              <ToolCallBlock key={i} tc={tc} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatTab() {
  const conferma = useConferma();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  async function reload() {
    try {
      const { data } = await axios.get("/api/sara/memory/conversations", {
        params: { channel: CHANNEL_INSUREASSIST, limit: 100 },
      });
      setMessages(data.items || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setText("");
    setSending(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: trimmed, created_at: new Date().toISOString() },
    ]);
    try {
      await axios.post("/api/sara/chat", { text: trimmed, channel: CHANNEL_INSUREASSIST });
      await reload();
    } catch (err) {
      toast.error("Errore di invio: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  }

  async function handleClear() {
    if (!(await conferma({
      titolo: "Cancellare la cronologia chat di questa sessione web?",
      descrizione: "La cronologia di questa chat verrà eliminata: Sara non ricorderà più questi scambi.",
    }))) return;
    try {
      await axios.delete("/api/sara/memory/conversations", {
        params: { channel: CHANNEL_INSUREASSIST },
      });
      setMessages([]);
      toast.success("Storia cancellata.");
    } catch (err) {
      toast.error("Errore: " + err.message);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-340px)] min-h-[420px]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline" className="font-mono text-[10px]">
            canale: insureassist
          </Badge>
          <span className="hidden sm:inline">
            Cronologia separata da Telegram — stessa memoria istruzioni.
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleClear} data-testid="sara-chat-clear-button">
          <Trash2 className="h-3.5 w-3.5 mr-2" />
          Cancella
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 space-y-4" data-testid="sara-chat-history">
        {loading && (
          <div className="text-sm text-muted-foreground text-center py-8">Caricamento…</div>
        )}
        {!loading && messages.length === 0 && (
          <Card className="p-6 text-center border-dashed">
            <Sparkles className="h-8 w-8 mx-auto mb-3 text-primary" />
            <h3 className="text-base font-semibold font-heading">Inizia una conversazione con Sara</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Prova con &quot;quanti clienti ho?&quot; oppure &quot;cerca il cliente Rossi&quot;.
              <br />
              Su Telegram puoi anche mandare vocali.
            </p>
          </Card>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={m.id || i} msg={m} />
        ))}
        {sending && (
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="bg-card border border-border rounded-2xl px-4 py-3 inline-flex items-center">
              <span className="inline-flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-end gap-3 border-t pt-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Scrivi a Sara… (Invio per inviare, Shift+Invio per a capo)"
          className="min-h-[44px] max-h-[200px] resize-none"
          rows={1}
          data-testid="sara-chat-input"
        />
        <Button onClick={handleSend} disabled={!text.trim() || sending} data-testid="sara-chat-send-button">
          <Send className="h-4 w-4 mr-2" />
          Invia
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
//  Memoria — istruzioni e conversazioni
// =====================================================================

const TYPE_BADGE = {
  preference: "preferenza",
  rule: "regola",
  recurring: "ricorrente",
  "one-time": "una tantum",
};

function MemoriaTab() {
  const conferma = useConferma();
  const [instructions, setInstructions] = useState([]);
  const [text, setText] = useState("");
  const [type, setType] = useState("preference");
  const [adding, setAdding] = useState(false);
  const [channel, setChannel] = useState("insureassist");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  async function reloadInstructions() {
    try {
      const { data } = await axios.get("/api/sara/memory/instructions");
      setInstructions(data.items || []);
    } catch (err) {
      toast.error("Errore caricamento istruzioni");
    }
  }

  async function reloadConversations(ch = channel) {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/sara/memory/conversations", {
        params: { channel: ch, limit: 100 },
      });
      setMessages(data.items || []);
    } catch (err) {
      toast.error("Errore caricamento conversazione");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reloadInstructions();
  }, []);
  useEffect(() => {
    reloadConversations(channel);
  }, [channel]);

  async function handleAdd() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      await axios.post("/api/sara/memory/instructions", { text: trimmed, type });
      setText("");
      setType("preference");
      await reloadInstructions();
      toast.success("Istruzione aggiunta.");
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id, content) {
    if (!(await conferma({
      titolo: "Disattivare l'istruzione?",
      descrizione: `"${content}"`,
    }))) return;
    try {
      await axios.delete(`/api/sara/memory/instructions/${id}`);
      await reloadInstructions();
      toast.success("Istruzione rimossa.");
    } catch (err) {
      toast.error("Errore: " + err.message);
    }
  }

  async function handleClearChannel() {
    if (!(await conferma({
      titolo: `Cancellare la storia del canale "${channel}"?`,
    }))) return;
    try {
      await axios.delete("/api/sara/memory/conversations", { params: { channel } });
      await reloadConversations(channel);
      toast.success("Storia cancellata.");
    } catch (err) {
      toast.error("Errore: " + err.message);
    }
  }

  const active = instructions.filter((i) => i.active);
  const inactive = instructions.filter((i) => !i.active);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[440px_1fr] gap-5">
      <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-4 w-4" />
            Istruzioni attive ({active.length})
          </CardTitle>
          <CardDescription>Sara le include sempre nel system prompt, su tutti i canali.</CardDescription>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-4">
          <div className="flex flex-col gap-2 bg-muted/30 rounded-lg p-3">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Es. preferisco riassunti brevi…"
              data-testid="sara-new-instruction-input"
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <div className="flex gap-2">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="flex-1" data-testid="sara-new-instruction-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="preference">Preferenza</SelectItem>
                  <SelectItem value="rule">Regola</SelectItem>
                  <SelectItem value="recurring">Ricorrente</SelectItem>
                  <SelectItem value="one-time">Una tantum</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAdd} disabled={adding || !text.trim()} data-testid="sara-add-instruction-button">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {active.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
                Nessuna istruzione attiva.
              </div>
            )}
            {active.map((i, idx) => (
              <div
                key={i.id}
                className="group flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                data-testid={`sara-instruction-card-${i.id}`}
              >
                <div className="text-xs text-muted-foreground font-mono pt-0.5 w-6 text-right shrink-0">
                  {idx + 1}.
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-5 break-words">{i.text}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge variant="secondary" className="text-[10px] uppercase">
                      {TYPE_BADGE[i.type] || i.type}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">
                      {formatDate(i.created_at, "—")}
                    </span>
                    <span className="text-[11px] text-muted-foreground">• {i.source || "user"}</span>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDelete(i.id, i.text)}
                  className="opacity-60 group-hover:opacity-100"
                  data-testid={`sara-instruction-delete-${i.id}`}
                 aria-label="Elimina">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {inactive.length > 0 && (
            <div className="pt-3 border-t">
              <div className="text-xs text-muted-foreground mb-2">Disattivate ({inactive.length})</div>
              <div className="space-y-1">
                {inactive.slice(0, 5).map((i) => (
                  <div key={i.id} className="text-xs text-muted-foreground line-through truncate">
                    {i.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-5 pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base">Cronologia conversazioni</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="h-9 w-[160px]" data-testid="sara-conversation-channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insureassist">Web (InsureAssist)</SelectItem>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="system">System (briefing)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => reloadConversations(channel)}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="destructive" onClick={handleClearChannel}>
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Cancella
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-8">Caricamento…</div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              Nessun messaggio nel canale &quot;{channel}&quot;.
            </div>
          ) : (
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-2">
              {messages.map((m, idx) => (
                <div
                  key={m.id || idx}
                  className="flex gap-3 items-start p-2 rounded-lg hover:bg-muted/30"
                >
                  <Badge
                    variant={m.role === "user" ? "default" : m.role === "tool" ? "secondary" : "outline"}
                    className="shrink-0 mt-0.5 text-[10px] font-mono"
                  >
                    {m.role}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm leading-5 break-words whitespace-pre-wrap">
                      {m.content || <span className="italic text-muted-foreground">(vuoto)</span>}
                    </div>
                    {Array.isArray(m.tool_calls) && m.tool_calls.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {m.tool_calls.map((tc, i) => (
                          <Badge key={i} variant="outline" className="font-mono text-[10px]">
                            {tc.function?.name || tc.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                      {new Date(m.created_at).toLocaleString("it-IT", {
                        dateStyle: "short",
                        timeStyle: "medium",
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================================
//  Log eventi
// =====================================================================

const LEVEL_TONE = {
  info: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function LogTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");

  async function reload() {
    try {
      const { data } = await axios.get("/api/sara/logs", { params: { limit: 150 } });
      setEvents(data.items || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    if (!autoRefresh) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (levelFilter !== "all" && e.level !== levelFilter) return false;
      if (!q) return true;
      const blob = (e.type + " " + JSON.stringify(e.data || {})).toLowerCase();
      return blob.includes(q);
    });
  }, [events, search, levelFilter]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtra per tipo o contenuto…"
                data-testid="sara-log-search"
              />
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="h-9 w-[140px]" data-testid="sara-log-level-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i livelli</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warn">Avviso</SelectItem>
                  <SelectItem value="error">Errore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAutoRefresh((a) => !a)}
                data-testid="sara-log-autorefresh-toggle"
              >
                {autoRefresh ? <Pause className="h-3.5 w-3.5 mr-2" /> : <Play className="h-3.5 w-3.5 mr-2" />}
                {autoRefresh ? "Pausa" : "Auto"}
              </Button>
              <Button size="sm" variant="outline" onClick={reload}>
                <RefreshCw className="h-3.5 w-3.5 mr-2" />
                Aggiorna
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-3 pt-0">
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-8">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">Nessun evento registrato.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((ev) => (
                <Collapsible key={ev.id}>
                  <CollapsibleTrigger
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50 text-left rounded-lg"
                    data-testid={`sara-log-row-${ev.id}`}
                  >
                    <Badge variant="outline" className={`shrink-0 text-[10px] uppercase ${LEVEL_TONE[ev.level] || ""}`}>
                      {ev.level || "info"}
                    </Badge>
                    <span className="font-mono text-xs shrink-0 text-muted-foreground">
                      {new Date(ev.created_at).toLocaleTimeString("it-IT", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                    <span className="font-mono text-xs font-semibold shrink-0">{ev.type}</span>
                    <span className="truncate text-xs text-muted-foreground flex-1">
                      {ev.data && Object.keys(ev.data).length > 0
                        ? JSON.stringify(ev.data).slice(0, 120)
                        : ""}
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-3 pb-2">
                      <div className="bg-muted/30 border rounded-lg p-3 flex items-start gap-2">
                        <pre className="flex-1 font-mono text-[11px] leading-5 whitespace-pre-wrap break-words">
                          {JSON.stringify(ev.data || {}, null, 2)}
                        </pre>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(ev.data || {}, null, 2));
                            toast.success("Copiato.");
                          }}
                         aria-label="Copia il testo">
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================================
//  Operativo \u2014 Bozze in attesa + WhatsApp non risposti
// =====================================================================

function OperativoTab() {
  const conferma = useConferma();
  const [drafts, setDrafts] = useState([]);
  const [pending, setPending] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [scanBusy, setScanBusy] = useState(false);
  const [newRemText, setNewRemText] = useState("");
  const [newRemWhen, setNewRemWhen] = useState("");
  const [newRemBusy, setNewRemBusy] = useState(false);

  async function reload() {
    try {
      const [d, p, s, pr] = await Promise.all([
        axios.get("/api/sara/drafts"),
        axios.get("/api/sara/whatsapp/pending"),
        axios.get("/api/sara/schedules"),
        axios.get("/api/sara/proactive/proposals").catch(() => ({ data: { items: [] } })),
      ]);
      setDrafts(d.data.items || []);
      setPending(p.data.items || []);
      setSchedules(s.data.items || []);
      setProposals(pr.data.items || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, []);

  async function handleSend(id) {
    // v3.9.12 — invio reale di un messaggio WhatsApp a un cliente: conferma esplicita
    if (!(await conferma({
      titolo: "Inviare questa bozza come messaggio WhatsApp al cliente?",
      descrizione:
        "Il messaggio parte subito e non può essere richiamato.",
    }))) return;
    setBusyId(id);
    try {
      await axios.post(`/api/sara/drafts/${id}/send`);
      toast.success("Bozza inviata.");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  }

  async function handleCancel(id) {
    setBusyId(id);
    try {
      await axios.post(`/api/sara/drafts/${id}/cancel`);
      toast.success("Bozza annullata.");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(d) {
    setEditingId(d.id);
    setEditText(d.text);
  }

  async function saveEdit() {
    if (!editingId) return;
    setBusyId(editingId);
    try {
      await axios.post(`/api/sara/drafts/${editingId}/update`, { text: editText });
      toast.success("Bozza aggiornata.");
      setEditingId(null);
      setEditText("");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  }

  async function deleteSchedule(id) {
    if (!(await conferma({
      titolo: "Cancellare definitivamente questa pianificazione?",
      descrizione: "La pianificazione verrà eliminata definitivamente.",
    }))) return;
    try {
      await axios.delete(`/api/sara/schedules/${id}`);
      toast.success("Pianificazione cancellata.");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  }

  async function runProactiveScan() {
    setScanBusy(true);
    try {
      const { data } = await axios.post("/api/sara/actions/proactive_scan");
      if (data.skipped) {
        toast.info("Scansione saltata: " + (data.reason || "fuori orario"));
      } else {
        toast.success("Scansione proattiva completata.");
      }
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setScanBusy(false);
    }
  }

  async function createQuickReminder() {
    if (!newRemText.trim() || !newRemWhen) {
      toast.error("Testo e data sono obbligatori.");
      return;
    }
    setNewRemBusy(true);
    try {
      await axios.post("/api/sara/schedules/one_time", {
        text: newRemText.trim(),
        when_iso: newRemWhen,
      });
      toast.success("Promemoria pianificato.");
      setNewRemText("");
      setNewRemWhen("");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setNewRemBusy(false);
    }
  }

  async function rejectProposal(id) {
    try {
      await axios.post(`/api/sara/proactive/proposals/${id}/reject`);
      toast.success("Proposta scartata.");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessagesSquare className="h-4 w-4" />
            Bozze WhatsApp in attesa ({drafts.length})
          </CardTitle>
          <CardDescription>
            Risposte preparate da Sara che aspettano la tua conferma (su Telegram o da qui).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-3">
          {loading ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : drafts.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
              Nessuna bozza in attesa.
            </div>
          ) : (
            drafts.map((d) => (
              <Card key={d.id} className="border" data-testid={`sara-draft-card-${d.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-semibold text-sm">{d.client_name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{d.jid}</div>
                    </div>
                    <Badge variant={d.status === "editing" ? "secondary" : "outline"}>{d.status}</Badge>
                  </div>
                  {editingId === d.id ? (
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      className="text-sm"
                      data-testid={`sara-draft-edit-${d.id}`}
                    />
                  ) : (
                    <div className="text-sm bg-muted/30 rounded p-2 italic leading-5">{d.text}</div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {editingId === d.id ? (
                      <>
                        <Button size="sm" onClick={saveEdit} disabled={busyId === d.id}>
                          Salva modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingId(null);
                            setEditText("");
                          }}
                        >
                          Annulla modifica
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSend(d.id)}
                          disabled={busyId === d.id}
                          data-testid={`sara-draft-send-${d.id}`}
                        >
                          <Send className="h-3.5 w-3.5 mr-2" /> Invia
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => startEdit(d)}>
                          <Wrench className="h-3.5 w-3.5 mr-2" /> Modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancel(d.id)}
                          disabled={busyId === d.id}
                          data-testid={`sara-draft-cancel-${d.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" /> Annulla
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-5 pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4" />
            WhatsApp non ancora risposti ({pending.length})
          </CardTitle>
          <CardDescription>
            Clienti che ti hanno scritto e a cui non hai ancora risposto. Promemoria automatico dopo 60 min.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-2">
          {loading ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : pending.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
              Tutto in pari. Bravo!
            </div>
          ) : (
            pending.map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-3 p-3 border rounded-lg"
                data-testid={`sara-pending-${p.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{p.client_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Ultimo messaggio {p.minutes_ago} min fa
                  </div>
                  {p.last_summary && (
                    <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2">{p.last_summary}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
      </div>

      <Card>
        <CardHeader className="p-5 pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Pianificazioni attive ({schedules.length})
              </CardTitle>
              <CardDescription>
                Promemoria e task ricorrenti che Sara eseguirà automaticamente.
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={runProactiveScan}
              disabled={scanBusy}
              data-testid="sara-proactive-scan-button"
              className="gap-2"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {scanBusy ? "Scansione in corso…" : "Esegui scan proattivo ora"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-3">
          {/* Quick create one-time reminder */}
          <div className="border rounded-lg p-3 bg-muted/20 space-y-2" data-testid="sara-quick-reminder-form">
            <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Aggiungi promemoria rapido
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
              <Input
                placeholder="Es. Chiamare il cliente Bianchi"
                value={newRemText}
                onChange={(e) => setNewRemText(e.target.value)}
                disabled={newRemBusy}
                data-testid="sara-quick-reminder-text"
              />
              <Input
                type="datetime-local"
                value={newRemWhen}
                onChange={(e) => setNewRemWhen(e.target.value)}
                disabled={newRemBusy}
                className="sm:w-[200px]"
                data-testid="sara-quick-reminder-when"
              />
              <Button
                onClick={createQuickReminder}
                disabled={newRemBusy || !newRemText.trim() || !newRemWhen}
                data-testid="sara-quick-reminder-submit"
              >
                {newRemBusy ? "…" : "Pianifica"}
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Per ricorrenti (es. ogni giorno) usa la chat: "Sara, ogni feriale alle 12 fammi un mini-briefing".
            </div>
          </div>

          {loading ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : schedules.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-lg">
              Nessuna pianificazione. Su Telegram puoi dire "Sara, ricordami alle 14:30 di…" oppure "ogni giorno alle 12 fammi…".
            </div>
          ) : (
            schedules.map((s) => (
              <div
                key={s.id}
                className="flex items-start gap-3 p-3 border rounded-lg"
                data-testid={`sara-schedule-${s.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  {s.type === "recurring" ? <RefreshCw className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={s.type === "recurring" ? "default" : "secondary"} className="text-[10px]">
                      {s.type === "recurring" ? "RICORRENTE" : "ONE-TIME"}
                    </Badge>
                    <span className="font-semibold text-sm">
                      {s.name || (s.action?.payload?.text || "").slice(0, 80)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                    {s.cron && <span className="font-mono">cron: {s.cron}</span>}
                    <span>Prossimo: <strong>{formatDateTime(s.next_fire_at, "—")}</strong></span>
                    <span>Eseguito {s.fired_count || 0} volte</span>
                  </div>
                  {s.action?.payload?.prompt && (
                    <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2">
                      Prompt: {s.action.payload.prompt}
                    </div>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => deleteSchedule(s.id)}
                  data-testid={`sara-schedule-delete-${s.id}`}
                  aria-label="Cancella"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {proposals.length > 0 && (
        <Card>
          <CardHeader className="p-5 pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Proposte proattive in attesa ({proposals.length})
            </CardTitle>
            <CardDescription>
              Sara ha identificato delle azioni da proporti. Confermale dal Telegram (consigliato) o scartale da qui.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5 pt-0 space-y-2">
            {proposals.map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-3 p-3 border rounded-lg"
                data-testid={`sara-proposal-${p.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">
                      {p.action_type === "send_whatsapp_draft" ? "BOZZA WA" :
                        p.action_type === "send_reminder" ? "PROMEMORIA" : "INFO"}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatDateTime(p.created_at, "—")}</span>
                  </div>
                  <div className="text-sm mt-1">{p.reason}</div>
                  {p.action_payload?.draft_text && (
                    <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2 bg-muted/30 p-2 rounded">
                      "{p.action_payload.draft_text}"
                    </div>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => rejectProposal(p.id)}
                  data-testid={`sara-proposal-reject-${p.id}`}
                  aria-label="Scarta"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =====================================================================
//  Blacklist WhatsApp — escludi le chat che Sara NON deve monitorare
// =====================================================================

function BlacklistTab() {
  const conferma = useConferma();
  const [blacklist, setBlacklist] = useState([]);
  const [knownContacts, setKnownContacts] = useState([]);
  const [blForm, setBlForm] = useState({ match_type: "phone", value: "", notes: "" });
  const [blBusy, setBlBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function reload() {
    try {
      const [bl, kc] = await Promise.all([
        axios.get("/api/sara/wa/blacklist"),
        axios.get("/api/sara/wa/known_contacts?days=30"),
      ]);
      setBlacklist(bl.data.items || []);
      setKnownContacts(kc.data.items || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, []);

  async function addBlacklist({ match_type, value, notes }) {
    if (!value || !value.trim()) {
      toast.error("Inserisci un valore.");
      return;
    }
    setBlBusy(true);
    try {
      await axios.post("/api/sara/wa/blacklist", {
        match_type,
        value: value.trim(),
        notes: (notes || "").trim(),
      });
      toast.success(`Aggiunto a blacklist (${match_type}): ${value}`);
      setBlForm({ match_type: "phone", value: "", notes: "" });
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setBlBusy(false);
    }
  }

  async function removeBlacklist(id, label) {
    if (!(await conferma({
      titolo: `Rimuovere "${label}" dalla blacklist?`,
    }))) return;
    try {
      await axios.delete(`/api/sara/wa/blacklist/${id}`);
      toast.success("Rimosso dalla blacklist.");
      await reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  }

  async function blockContact(contact) {
    if (!(await conferma({
      titolo: `Bloccare ${contact.client_name} (${contact.phone})?`,
      descrizione: "Sara non leggerà più i suoi messaggi.",
    }))) return;
    await addBlacklist({
      match_type: "jid",
      value: contact.jid,
      notes: `${contact.client_name} (bloccato da UI)`,
    });
  }

  return (
    <Card>
      <CardHeader className="p-5 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Ban className="h-4 w-4" />
          Blacklist WhatsApp ({blacklist.length})
        </CardTitle>
        <CardDescription>
          Contatti, numeri o nomi che Sara <strong>ignorerà completamente</strong> (nessuna lettura, nessuna trascrizione, nessuna cronologia).
          I gruppi WhatsApp sono già esclusi di default.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-5 pt-0 space-y-4">
        {/* Form aggiunta manuale */}
        <div className="border rounded-lg p-3 bg-muted/20 space-y-2" data-testid="sara-bl-form">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <Select
              value={blForm.match_type}
              onValueChange={(v) => setBlForm({ ...blForm, match_type: v })}
            >
              <SelectTrigger className="sm:col-span-3" data-testid="sara-bl-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Numero</SelectItem>
                <SelectItem value="name">Nome</SelectItem>
                <SelectItem value="jid">JID completo</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="sm:col-span-5"
              placeholder={
                blForm.match_type === "phone"
                  ? "es. 3331234567 (anche +39)"
                  : blForm.match_type === "name"
                  ? "es. Spam Bot (case-insensitive, contains)"
                  : "es. 393331234567@s.whatsapp.net"
              }
              value={blForm.value}
              onChange={(e) => setBlForm({ ...blForm, value: e.target.value })}
              data-testid="sara-bl-value"
            />
            <Input
              className="sm:col-span-4"
              placeholder="note (opzionale)"
              value={blForm.notes}
              onChange={(e) => setBlForm({ ...blForm, notes: e.target.value })}
              data-testid="sara-bl-notes"
            />
          </div>
          <Button
            onClick={() => addBlacklist(blForm)}
            disabled={blBusy || !blForm.value.trim()}
            data-testid="sara-bl-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> {blBusy ? "Aggiungo…" : "Aggiungi a blacklist"}
          </Button>
        </div>

        {/* In blacklist ora */}
        {blacklist.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              In blacklist ({blacklist.length})
            </div>
            {blacklist.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-3 p-3 border rounded-lg bg-red-50/30 dark:bg-red-950/10"
                data-testid={`sara-bl-entry-${b.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                  <UserX className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {b.match_type}
                    </Badge>
                    <span className="font-mono text-sm break-all">{b.value}</span>
                  </div>
                  {b.notes && (
                    <div className="text-[11px] text-muted-foreground italic mt-0.5">{b.notes}</div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Aggiunto {formatDateTime(b.added_at, "—")}
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeBlacklist(b.id, `${b.match_type}: ${b.value}`)}
                  data-testid={`sara-bl-remove-${b.id}`}
                 aria-label="Elimina">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Contatti visti — per quick-add */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Contatti recenti (ultimi 30 giorni) — clicca per bloccare
          </div>
          {knownContacts.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
              Nessun contatto WhatsApp ricevuto negli ultimi 30 giorni.
            </div>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
              {knownContacts
                .filter((c) => {
                  // Nascondi quelli già in blacklist per JID
                  return !blacklist.some(
                    (b) =>
                      (b.match_type === "jid" && b.value === c.jid) ||
                      (b.match_type === "phone" && c.phone && c.phone.replace(/\D/g, "").endsWith(b.value))
                  );
                })
                .map((c) => (
                  <div
                    key={c.jid}
                    className="flex items-center gap-3 p-2.5 border rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`sara-bl-contact-${c.jid}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Phone className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.client_name}</div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{c.phone}</span>
                        <span>•</span>
                        <span>{c.msg_count} msg</span>
                        <span>•</span>
                        <span>ultimo {formatDateTime(c.last_msg_at, "—")}</span>
                      </div>
                      {c.last_text && (
                        <div className="text-[11px] text-muted-foreground italic truncate mt-0.5">
                          "{c.last_text}"
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => blockContact(c)}
                      className="shrink-0"
                      data-testid={`sara-bl-block-${c.jid}`}
                    >
                      <Ban className="h-3.5 w-3.5 mr-1" /> Blocca
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
//  Pagina principale Sara AI
// =====================================================================

export default function SaraPage() {
  const [globalStatus, setGlobalStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get("/api/sara/status");
        if (!cancelled) setGlobalStatus(data);
      } catch {
        if (!cancelled) setGlobalStatus({ ok: false });
      }
    }
    load();
    const t = setInterval(load, 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const overallTone = globalStatus?.ok ? "ok" : globalStatus?.error ? "bad" : "warn";
  const overallLabel = globalStatus?.ok ? "Operativo" : globalStatus?.error ? "Servizi degradati" : "In avvio";

  return (
    <div className="space-y-6" data-testid="sara-ai-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-primary" />
            Sara AI
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Il tuo assistente per il lavoro assicurativo — attivo su Telegram, WhatsApp ed Email.
          </p>
        </div>
        <Badge variant="outline" className="gap-2 px-3 py-1.5" data-testid="sara-global-status-pill">
          <StatusDot tone={overallTone} />
          <span className="text-sm font-medium">{overallLabel}</span>
        </Badge>
      </div>

      <Tabs defaultValue="chat" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 max-w-[860px]">
          <TabsTrigger value="chat" className="flex items-center gap-2" data-testid="sara-tab-chat">
            <MessagesSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Chat</span>
          </TabsTrigger>
          <TabsTrigger value="operativo" className="flex items-center gap-2" data-testid="sara-tab-operativo">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Operativo</span>
          </TabsTrigger>
          <TabsTrigger value="memoria" className="flex items-center gap-2" data-testid="sara-tab-memoria">
            <Brain className="h-4 w-4" />
            <span className="hidden sm:inline">Memoria</span>
          </TabsTrigger>
          <TabsTrigger value="blacklist" className="flex items-center gap-2" data-testid="sara-tab-blacklist">
            <Ban className="h-4 w-4" />
            <span className="hidden sm:inline">Blacklist</span>
          </TabsTrigger>
          <TabsTrigger value="stato" className="flex items-center gap-2" data-testid="sara-tab-stato">
            <Cpu className="h-4 w-4" />
            <span className="hidden sm:inline">Stato</span>
          </TabsTrigger>
          <TabsTrigger value="log" className="flex items-center gap-2" data-testid="sara-tab-log">
            <ListTree className="h-4 w-4" />
            <span className="hidden sm:inline">Log</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <ChatTab />
        </TabsContent>
        <TabsContent value="operativo">
          <OperativoTab />
        </TabsContent>
        <TabsContent value="memoria">
          <MemoriaTab />
        </TabsContent>
        <TabsContent value="blacklist">
          <BlacklistTab />
        </TabsContent>
        <TabsContent value="stato">
          <StatoTab />
        </TabsContent>
        <TabsContent value="log">
          <LogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
